import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeApp } from "firebase/app";
import { getMessaging, onMessage } from "firebase/messaging";
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { pushNotificationLocalStorageItems } from '@Components/Utilities/SandhillsConstants';

const FirebaseContext = createContext();
export const FirebaseProvider = ({ firebaseConfig, vapidKey, children }) => {
    if (!firebaseConfig || !vapidKey) {
        return children;
    }
    const routes = useRecoilValue(atoms.routes);
    let firebaseApp = null;
    let firebaseMessaging = null;

    if (typeof window !== 'undefined' && !firebaseApp) {
        firebaseApp = initializeApp(firebaseConfig);
        firebaseMessaging = getMessaging(firebaseApp);
    }
    
    const [firebase, setFirebase] = useState({
        firebaseMessaging: firebaseMessaging, 
        vapidKey: vapidKey
    });

    const saveIterableAction = (data) => {
        sandhillsRequest({
            url: routes.SaveAction,
            method: 'post',
            data: {
                email: data?.email,
                actionName: 'webPushClick',
                additionalProperties: [{
                    key: 'messageId',
                    value: data?.messageId
                }, {
                    key: 'campaignId',
                    value: data?.campaignId
                }
                ],
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        });
    };

    const isSafeUrl = (url) => {
        try {
            const parsed = new URL(url, window.location.origin);
            // Only allow URLs from your domain
            return parsed.hostname === window.location.hostname;
        } catch {
            return false;
        }
    };

    const removeOneSignalLocalStorageItems = () => {
        try {
            localStorage?.removeItem(pushNotificationLocalStorageItems.IsPushNotificationsEnabled);
            localStorage?.removeItem(pushNotificationLocalStorageItems.IsOptedOut);
            localStorage?.removeItem("os_pageViews");
        } catch (err) {
            console.warn('Could not remove OneSignal local storage items:', err);
        }
    };

    const registerServiceWorker = async (vapidKey) => {
        if (!('serviceWorker' in navigator)) {
            console.warn('Service workers not supported in this browser');
            return null;
        }

        try {
            // Remove any OneSignal local storage items to prevent conflicts with Firebase messaging
            removeOneSignalLocalStorageItems();

            // First, check all existing registrations
            const registrations = await navigator.serviceWorker.getRegistrations();

            // Check if Firebase messaging service worker is already registered
            let firebaseRegistration = null;

            for (let registration of registrations) {
                if (registration.active) {
                    const scriptURL = registration.active.scriptURL || '';
                    // Check for Firebase messaging service worker
                    if (scriptURL.includes('firebase-messaging-sw.js')) {
                        firebaseRegistration = registration;
                        console.log('Firebase service worker already registered:', registration);
                    }
                    // Check for conflicting OneSignal service worker
                    if (scriptURL.includes('OneSignalSDKWorker.js')) {
                        try {
                            await registration.unregister();
                            console.log('Unregistered OneSignal service worker');
                        } catch (e) {
                            console.warn('Failed to unregister OneSignal service worker:', e);
                        }
                    }
                }
            }

            // Only register if Firebase SW is not already registered
            if (!firebaseRegistration) {
                try {
                    firebaseRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/firebase-cloud-messaging-push-scope' });
                    console.log('Firebase service worker registered:', firebaseRegistration);
                } catch (regErr) {
                    console.warn('Firebase service worker registration failed:', regErr);
                    return null;
                }
            }

            // Ensure the service worker is ready (whether newly registered or existing)
            try {
                await navigator.serviceWorker.ready;
                console.log('Firebase service worker ready');
            } catch (readyErr) {
                console.warn('navigator.serviceWorker.ready failed:', readyErr);
            }

            return firebaseRegistration;
        } catch (error) {
            console.error('Service worker registration/check failed:', error);
            // Do not throw � return null so initialization can continue
            return null;
        }
    };

    useEffect(() => {
        setFirebase({
            firebaseMessaging: firebaseMessaging,
            vapidKey: vapidKey
        });

        registerServiceWorker(vapidKey);

        const handleMessage = (event) => {
            // Handle the message received from the service worker
            if (event.data.messageType === "notification-clicked") {
                saveIterableAction(event.data?.data);
            }
        }

        if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleMessage);
        }

        //Handle foreground messages
        const handleForegroundMessages = onMessage(firebaseMessaging, (payload) => {
            //show notification using the browser's Notification API
            if (Notification.permission === "granted") {
                const { title, ...options } = payload.notification;
                const notification = new Notification(title, options);
                notification.onclick = (event) => {
                    event.preventDefault();
                    const clickAction = payload.notification?.click_action;
                    if (clickAction && isSafeUrl(clickAction)) {
                        window.open(clickAction, '_self');
                    }
                    saveIterableAction(payload.data);
                };
            }
        });

        return () => {
            handleForegroundMessages();
        };
    }, []);

    return (
        <FirebaseContext.Provider value={firebase}>
            {children}
        </FirebaseContext.Provider>
    );
};

export const useFirebaseMessaging = () => {
    return useContext(FirebaseContext);
};